import { formatStoredPublishError } from '../shared/x-errors';

export { formatStoredPublishError };

/**
 * Shared date/time/duration formatting for the dashboard. Pure browser
 * `toLocaleString`/`Intl` — no external libs, no timezone conversion beyond
 * what the browser already does for you.
 *
 * `locale`/`timeZone` overrides exist only so tests can pin deterministic
 * output; production call sites should omit them and let the browser's own
 * locale/timezone apply.
 */

export interface FormatOpts {
  locale?: string;
  timeZone?: string;
  /** Reference "now" — override in tests only. */
  now?: Date;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "Jul 28, 7:42 PM" (this year) or "Jul 28 2025, 7:42 PM" (other years). */
export function formatDateTime(iso: string | null | undefined, opts: FormatOpts = {}): string {
  const d = toDate(iso);
  if (!d) return '—';
  const now = opts.now ?? new Date();

  const yearFmt = new Intl.DateTimeFormat(opts.locale, { timeZone: opts.timeZone, year: 'numeric' });
  const dateFmt = new Intl.DateTimeFormat(opts.locale, {
    timeZone: opts.timeZone,
    month: 'short',
    day: 'numeric',
  });
  const timeFmt = new Intl.DateTimeFormat(opts.locale, {
    timeZone: opts.timeZone,
    hour: 'numeric',
    minute: '2-digit',
  });

  const datePart = dateFmt.format(d);
  const timePart = timeFmt.format(d);
  const sameYear = yearFmt.format(d) === yearFmt.format(now);

  return sameYear ? `${datePart}, ${timePart}` : `${datePart} ${yearFmt.format(d)}, ${timePart}`;
}

/** "2m ago", "3h ago", "yesterday" — falls back to formatDateTime beyond 7 days. */
export function formatRelative(iso: string | null | undefined, opts: FormatOpts = {}): string {
  const d = toDate(iso);
  if (!d) return '—';
  const now = opts.now ?? new Date();
  const diffMs = now.getTime() - d.getTime();

  // future timestamps (clock skew, next-run estimates) — just show the absolute time.
  if (diffMs < 0) return formatDateTime(iso, opts);
  if (diffMs < MINUTE_MS) return 'just now';
  if (diffMs < HOUR_MS) return `${Math.floor(diffMs / MINUTE_MS)}m ago`;
  if (diffMs < DAY_MS) return `${Math.floor(diffMs / HOUR_MS)}h ago`;
  if (diffMs < 2 * DAY_MS) return 'yesterday';
  if (diffMs < 7 * DAY_MS) return `${Math.floor(diffMs / DAY_MS)}d ago`;
  return formatDateTime(iso, opts);
}

/** "4.2s", "1m 12s" — ms is a plain duration, not a timestamp. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;

  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) {
    const rounded = Math.round(totalSeconds * 10) / 10;
    return `${rounded}s`;
  }

  const wholeSeconds = Math.round(totalSeconds);
  const m = Math.floor(wholeSeconds / 60);
  const s = wholeSeconds % 60;
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

/** Elapsed wall time of a run, or "—" while it is still going. */
export function formatRunDuration(run: {
  startedAt: string;
  finishedAt?: string | null;
}): string {
  if (!run.finishedAt) return '—';
  const ms = new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime();
  return formatDuration(ms);
}

/** "4:00 PM" for a raw 0-23 hour — a wall-clock label, not a real tz conversion. */
export function formatHour(hour: number, opts: Pick<FormatOpts, 'locale'> = {}): string {
  const d = new Date(2000, 0, 1, hour, 0, 0, 0);
  return new Intl.DateTimeFormat(opts.locale, { hour: 'numeric', minute: '2-digit' }).format(d);
}

/** "$0.0421" for a numeric-ish cost value, "—" for null/NaN. */
export function money(v: string | number | null): string {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? `$${n.toFixed(4)}` : '—';
}
